import React, { useState, useRef } from 'react';
import { Upload, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';
import JSZip from 'jszip';

// Set up PDF.js worker for Create React App
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface FileUploadProps {
  onFileSelect: (file: File, pages: number) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Function to count PDF pages using multiple methods
  const countPdfPages = async (file: File): Promise<number> => {
    try {
      console.log('Processing PDF file:', file.name, 'Size:', file.size);
      const arrayBuffer = await file.arrayBuffer();
      
      // Method 1: Try pdf-lib first (most reliable)
      try {
        console.log('Trying pdf-lib method...');
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const pageCount = pdfDoc.getPageCount();
        console.log('pdf-lib page count:', pageCount);
        if (pageCount > 0) {
          return pageCount;
        }
      } catch (pdfLibError) {
        console.log('pdf-lib failed:', pdfLibError);
      }
      
      // Method 2: Try react-pdf/pdfjs
      try {
        console.log('Trying react-pdf method...');
        const pdf = await pdfjs.getDocument({ 
          data: arrayBuffer,
          useSystemFonts: true,
          disableFontFace: false
        }).promise;
        console.log('React-pdf page count:', pdf.numPages);
        if (pdf.numPages > 0) {
          return pdf.numPages;
        }
      } catch (pdfError) {
        console.log('React-pdf failed:', pdfError);
      }
      
      // Method 3: Manual PDF parsing
      try {
        console.log('Trying manual parsing method...');
        const text = new TextDecoder().decode(arrayBuffer);
        
        // Look for PDF page count in catalog
        const catalogMatch = text.match(/\/Count\s+(\d+)/);
        if (catalogMatch) {
          const count = parseInt(catalogMatch[1]);
          console.log('Found page count in catalog:', count);
          if (count > 0) return count;
        }
        
        // Look for individual page objects
        const pageMatches = text.match(/\/Type\s*\/Page(?!\w)/g);
        if (pageMatches && pageMatches.length > 0) {
          console.log('Found pages by object type:', pageMatches.length);
          if (pageMatches.length > 0) return pageMatches.length;
        }
        
        // Look for page references
        const pageRefMatches = text.match(/\d+\s+0\s+obj[^]*?\/Type\s*\/Page/g);
        if (pageRefMatches && pageRefMatches.length > 0) {
          console.log('Found pages by references:', pageRefMatches.length);
          if (pageRefMatches.length > 0) return pageRefMatches.length;
        }
      } catch (parseError) {
        console.log('Manual parsing failed:', parseError);
      }
      
      throw new Error('All PDF parsing methods failed');
      
    } catch (error) {
      console.error('Error counting PDF pages:', error);
      // Enhanced fallback estimation based on file size
      const sizeInKB = file.size / 1024;
      let estimatedPages;
      
      if (sizeInKB < 30) {
        estimatedPages = 1;
      } else if (sizeInKB < 100) {
        estimatedPages = Math.ceil(sizeInKB / 40); // More conservative for small files
      } else if (sizeInKB < 500) {
        estimatedPages = Math.ceil(sizeInKB / 60); // Medium files
      } else {
        estimatedPages = Math.ceil(sizeInKB / 80); // Large files
      }
      
      console.log('Using fallback estimation:', estimatedPages, 'pages for', sizeInKB, 'KB');
      return Math.max(1, estimatedPages);
    }
  };

  // Function to count DOCX pages by analyzing content
  const countDocxPages = async (file: File): Promise<number> => {
    try {
      console.log('Processing DOCX file:', file.name, 'Size:', file.size);
      const arrayBuffer = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);
      
      // Method 1: Try to get document statistics
      const docPropsApp = zip.files['docProps/app.xml'];
      if (docPropsApp) {
        const content = await docPropsApp.async('text');
        const pageMatch = content.match(/<Pages>(\d+)<\/Pages>/);
        if (pageMatch) {
          const pageCount = parseInt(pageMatch[1]);
          console.log('Found page count in docProps/app.xml:', pageCount);
          if (pageCount > 0) return pageCount;
        }
      }
      
      // Method 2: Analyze document.xml content
      const documentXml = zip.files['word/document.xml'];
      if (documentXml) {
        const content = await documentXml.async('text');
        
        // Look for page breaks
        const pageBreaks = content.match(/<w:br[^>]*w:type="page"[^>]*\/>/g) || [];
        const sectionBreaks = content.match(/<w:sectPr[^>]*>/g) || [];
        
        // Count paragraphs and estimate pages
        const paragraphs = content.match(/<w:p[^>]*>/g) || [];
        const tables = content.match(/<w:tbl[^>]*>/g) || [];
        
        // Calculate estimated pages based on content
        let estimatedPages = 1; // Start with 1 page
        estimatedPages += pageBreaks.length; // Add explicit page breaks
        estimatedPages += Math.max(0, sectionBreaks.length - 1); // Add section breaks (minus 1 as first section doesn't add page)
        
        // Estimate based on content density
        const contentElements = paragraphs.length + (tables.length * 3); // Tables take more space
        const additionalPages = Math.floor(contentElements / 25); // Roughly 25 elements per page
        estimatedPages += additionalPages;
        
        console.log('DOCX analysis:', {
          pageBreaks: pageBreaks.length,
          sectionBreaks: sectionBreaks.length,
          paragraphs: paragraphs.length,
          tables: tables.length,
          estimatedPages
        });
        
        return Math.max(1, estimatedPages);
      }
      
      throw new Error('Could not parse DOCX content');
      
    } catch (error) {
      console.error('Error counting DOCX pages:', error);
      
      // Fallback estimation based on file size with better heuristics for DOCX
      const sizeInKB = file.size / 1024;
      let estimatedPages;
      
      if (sizeInKB < 20) {
        estimatedPages = 1;
      } else if (sizeInKB < 50) {
        estimatedPages = Math.ceil(sizeInKB / 20); // Small docs: ~20KB per page
      } else if (sizeInKB < 200) {
        estimatedPages = Math.ceil(sizeInKB / 30); // Medium docs: ~30KB per page
      } else if (sizeInKB < 500) {
        estimatedPages = Math.ceil(sizeInKB / 40); // Large docs: ~40KB per page
      } else {
        estimatedPages = Math.ceil(sizeInKB / 50); // Very large docs: ~50KB per page
      }
      
      console.log('Using DOCX fallback estimation:', estimatedPages, 'pages for', sizeInKB, 'KB');
      return Math.max(1, estimatedPages);
    }
  };

  // Function to estimate pages for other file types
  const estimatePages = async (file: File): Promise<number> => {
    // For DOCX files, use specialized counting
    if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return await countDocxPages(file);
    }
    
    // For text files, count actual content
    if (file.type === 'text/plain') {
      try {
        const text = await file.text();
        const lines = text.split('\n').length;
        const words = text.split(/\s+/).length;
        
        // Better estimation for text files
        const pagesFromLines = Math.ceil(lines / 40); // ~40 lines per page
        const pagesFromWords = Math.ceil(words / 400); // ~400 words per page
        
        const estimatedPages = Math.max(pagesFromLines, pagesFromWords);
        console.log('Text file analysis:', { lines, words, estimatedPages });
        
        return Math.max(1, estimatedPages);
      } catch (error) {
        const sizeInKB = file.size / 1024;
        return Math.max(1, Math.ceil(sizeInKB / 3)); // Fallback: 3KB per page
      }
    }
    
    // For legacy DOC files (binary format)
    if (file.type === 'application/msword') {
      const sizeInKB = file.size / 1024;
      let estimatedPages;
      
      if (sizeInKB < 30) {
        estimatedPages = 1;
      } else if (sizeInKB < 100) {
        estimatedPages = Math.ceil(sizeInKB / 25); // DOC files: ~25KB per page
      } else {
        estimatedPages = Math.ceil(sizeInKB / 35); // Larger DOC files: ~35KB per page
      }
      
      console.log('DOC file estimation:', estimatedPages, 'pages for', sizeInKB, 'KB');
      return Math.max(1, estimatedPages);
    }
    
    return 1; // Default
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (file && (file.type === 'application/pdf' || 
                 file.type === 'application/msword' || 
                 file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                 file.type === 'text/plain')) {
      
      setIsProcessing(true);
      setSelectedFile(file);
      
      try {
        let pageCount: number;
        
        console.log('Processing file:', file.name, 'Type:', file.type, 'Size:', file.size);
        
        if (file.type === 'application/pdf') {
          // Count PDF pages using enhanced method
          pageCount = await countPdfPages(file);
          console.log('Final page count for PDF:', pageCount);
        } else {
          // Estimate pages for other file types
          pageCount = await estimatePages(file);
          console.log('Estimated page count for non-PDF:', pageCount);
        }
        
        setIsUploaded(true);
        setIsProcessing(false);
        onFileSelect(file, pageCount);
        
      } catch (error) {
        console.error('Error processing file:', error);
        setIsProcessing(false);
        alert('Error processing file. Please try again.');
      }
    } else {
      alert('Please upload a PDF, Word, or text file.');
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const dropzoneStyle: React.CSSProperties = {
    border: isDragOver ? '2px dashed #3b82f6' : '2px dashed #cbd5e1',
    borderRadius: '16px',
    padding: '3rem 2rem',
    textAlign: 'center',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    backgroundColor: isDragOver ? 'rgba(59, 130, 246, 0.05)' : 'white',
    transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
    boxShadow: isDragOver ? '0 10px 25px -5px rgba(59, 130, 246, 0.1)' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden'
  };

  const buttonStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    fontWeight: '600',
    padding: '12px 32px',
    borderRadius: '8px',
    border: 'none',
    cursor: isProcessing ? 'not-allowed' : 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
    opacity: isProcessing ? 0.7 : 1,
    marginTop: '1.5rem',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '160px'
  };

  if (isProcessing) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1rem', background: 'white', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', animation: 'fadeIn 0.5s ease' }}>
        <div style={{ width: '64px', height: '64px', backgroundColor: '#eff6ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)' }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        </div>
        <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b', margin: '0 0 0.5rem 0' }}>Processing File...</h3>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>Analyzing pages, please wait</p>
        <style>
          {`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          `}
        </style>
      </div>
    );
  }

  if (isUploaded && selectedFile) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1rem', background: 'white', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', animation: 'fadeIn 0.5s ease' }}>
        <div style={{ width: '64px', height: '64px', backgroundColor: '#f0fdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)' }}>
          <CheckCircle style={{ width: '32px', height: '32px', color: '#16a34a' }} />
        </div>
        <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b', margin: '0 0 0.5rem 0' }}>Upload Successful!</h3>
        <p style={{ fontSize: '14px', color: '#475569', marginBottom: '1.5rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px', margin: '0 auto 1.5rem auto' }}>{selectedFile.name}</p>
        <button 
          onClick={() => {
            setIsUploaded(false);
            setSelectedFile(null);
            setIsProcessing(false);
          }}
          style={{ background: 'transparent', color: '#3b82f6', border: '1px solid #bfdbfe', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s', fontSize: '14px' }}
          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.borderColor = '#93c5fd'; }}
          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = '#bfdbfe'; }}
        >
          Upload Different File
        </button>
      </div>
    );
  }

  return (
    <div style={{ animation: 'fadeIn 0.5s ease' }}>
      <div
        style={dropzoneStyle}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleSelectFile}
      >
        <div style={{ width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', backgroundColor: isDragOver ? '#dbeafe' : '#f8fafc', transition: 'background-color 0.3s' }}>
          <Upload style={{ width: '32px', height: '32px', color: isDragOver ? '#2563eb' : '#94a3b8' }} />
        </div>
        
        <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b', margin: '0 0 0.5rem 0' }}>
          Drop your file here
        </h3>
        <p style={{ fontSize: '14px', color: '#64748b', margin: '0' }}>or click to browse from your device</p>
        
        <button 
          onClick={(e) => { e.stopPropagation(); handleSelectFile(); }}
          disabled={isProcessing}
          style={buttonStyle}
          onMouseOver={(e) => { if(!isProcessing) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(37, 99, 235, 0.4)'; } }}
          onMouseOut={(e) => { if(!isProcessing) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.3)'; } }}
        >
          {isProcessing ? 'Processing...' : 'Select File'}
        </button>
        
        <p style={{ fontSize: '12px', color: '#94a3b8', margin: '1.5rem 0 0 0', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
          Supports PDF, DOCX, TXT
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.txt"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        aria-label="Select file for printing"
        title="Select file for printing"
      />
      <style>
        {`
          @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        `}
      </style>
    </div>
  );
};
